import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  consultationApi,
  formatConsultDate,
  formatFee,
  statusLabel,
} from '../../api/consultationApi';
import '../../styles/consultation.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DURATION_OPTIONS = [15, 30, 45, 60, 90];

function BookingActions({ booking, onChanged, isAdvocate }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const act = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="consult-actions" style={{ marginTop: 10 }}>
      {booking.joinableNow && (
        <button
          type="button"
          className="consult-btn primary"
          onClick={() => navigate(`/video-consultation/${booking._id}`)}
        >
          Join Video Consultation
        </button>
      )}
      {isAdvocate && (booking.status === 'pending' || booking.status === 'paid') && (
        <>
          <button
            type="button"
            className="consult-btn primary"
            disabled={busy}
            onClick={() => act(() => consultationApi.acceptBooking(booking._id), 'Consultation confirmed')}
          >
            Accept
          </button>
          <button
            type="button"
            className="consult-btn secondary"
            disabled={busy}
            onClick={() => act(() => consultationApi.rejectBooking(booking._id, 'Rejected by advocate'), 'Consultation rejected')}
          >
            Reject
          </button>
        </>
      )}
      {!['completed', 'cancelled', 'no_show', 'in_progress'].includes(booking.status) && (
        <button
          type="button"
          className="consult-btn secondary"
          disabled={busy}
          onClick={() => act(() => consultationApi.cancelBooking(booking._id, 'Cancelled by user'), 'Consultation cancelled')}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

function BookingCard({ booking, counterpart, isAdvocate, onChanged }) {
  return (
    <div className="consult-booking-card">
      <div className="consult-booking-top">
        <div>
          <div className="consult-booking-title notranslate" translate="no">
            {counterpart
              ? `${isAdvocate ? '' : 'Adv. '}${counterpart.firstName} ${counterpart.lastName}`
              : 'Consultation'}
          </div>
          <div className="consult-meta">
            {formatConsultDate(booking.scheduledStart, booking.timezone)} · {booking.durationMinutes} min
            <br />
            Fee: {formatFee(booking.fee, booking.currency)} · Payment: {statusLabel(booking.paymentStatus)}
          </div>
        </div>
        <span className={`consult-badge ${booking.displayStatus}`}>{statusLabel(booking.displayStatus)}</span>
      </div>
      <BookingActions booking={booking} onChanged={onChanged} isAdvocate={isAdvocate} />
    </div>
  );
}

const AdvocateConsultationPanel = () => {
  const [tab, setTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        consultationApi.getSettings('video'),
        consultationApi.getAdvocateBookings('video'),
      ]);
      setSettings(s.data.settings);
      setBookings(b.data.bookings || []);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Unable to load video consultation settings');
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const requests = bookings.filter((x) => x.status === 'pending' || x.status === 'paid');
    const upcoming = bookings.filter((x) => x.status === 'confirmed' || x.status === 'in_progress');
    const completed = bookings.filter((x) => x.status === 'completed');
    const history = bookings.filter((x) => ['completed', 'cancelled', 'no_show'].includes(x.status));
    return { upcoming, requests, completed, history };
  }, [bookings]);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const toggleDuration = (value) => {
    const current = settings.durations || [];
    const next = current.includes(value)
      ? current.filter((d) => d !== value)
      : [...current, value].sort((a, b) => a - b);
    update({ durations: next.length ? next : [30] });
  };

  const updateDay = (dayOfWeek, patch) => {
    const weekly = (settings.weeklyAvailability || []).map((d) =>
      d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d
    );
    update({ weeklyAvailability: weekly });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await consultationApi.saveSettings({
        ...settings,
        consultationType: 'video',
      });
      setSettings(res.data.settings);
      toast.success('Video consultation settings saved');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Unable to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="consult-panel"><p>Loading video consultation settings…</p></div>;

  return (
    <div className="consult-panel">
      <h3>Video Consultation</h3>
      <div className="consult-tabs">
        <button type="button" className={`consult-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>Settings</button>
        <button type="button" className={`consult-tab ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>Requests ({grouped.requests.length})</button>
        <button type="button" className={`consult-tab ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')}>Upcoming ({grouped.upcoming.length})</button>
        <button type="button" className={`consult-tab ${tab === 'completed' ? 'active' : ''}`} onClick={() => setTab('completed')}>Completed</button>
        <button type="button" className={`consult-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>History</button>
      </div>

      {tab === 'settings' && (
        <>
          <div className="consult-toggle">
            <div>
              <strong>Enable video consultations</strong>
              <div className="consult-muted">Only registered and approved advocates can offer this service.</div>
            </div>
            <label className="consult-switch">
              <input
                type="checkbox"
                checked={Boolean(settings.enabled)}
                onChange={(e) => update({ enabled: e.target.checked })}
              />
              <span />
            </label>
          </div>

          <div className="consult-field">
            <label className="consult-label">Consultation fee ({settings.currency || 'INR'})</label>
            <input
              type="number"
              min="0"
              value={settings.fee ?? 0}
              onChange={(e) => update({ fee: Number(e.target.value) })}
            />
          </div>

          <div className="consult-field">
            <label className="consult-label">Consultation durations</label>
            <div className="consult-duration-row">
              {DURATION_OPTIONS.map((d) => (
                <button
                  type="button"
                  key={d}
                  className={`consult-chip ${(settings.durations || []).includes(d) ? 'selected' : ''}`}
                  onClick={() => toggleDuration(d)}
                >
                  {d} min
                </button>
              ))}
            </div>
          </div>

          <div className="consult-toggle">
            <div>
              <strong>Require my acceptance</strong>
              <div className="consult-muted">If off, paid bookings are confirmed automatically.</div>
            </div>
            <label className="consult-switch">
              <input
                type="checkbox"
                checked={Boolean(settings.requireAcceptance)}
                onChange={(e) => update({ requireAcceptance: e.target.checked })}
              />
              <span />
            </label>
          </div>

          <label className="consult-label">Weekly availability</label>
          {(settings.weeklyAvailability || []).map((day) => (
            <div className="consult-week-row" key={day.dayOfWeek}>
              <strong>{DAY_NAMES[day.dayOfWeek]}</strong>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(day.enabled)}
                  onChange={(e) => updateDay(day.dayOfWeek, { enabled: e.target.checked })}
                /> On
              </label>
              <input
                type="time"
                value={day.startTime || '10:00'}
                onChange={(e) => updateDay(day.dayOfWeek, { startTime: e.target.value })}
                disabled={!day.enabled}
              />
              <input
                type="time"
                value={day.endTime || '18:00'}
                onChange={(e) => updateDay(day.dayOfWeek, { endTime: e.target.value })}
                disabled={!day.enabled}
              />
            </div>
          ))}

          <div className="consult-actions">
            <button type="button" className="consult-btn primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </>
      )}

      {tab !== 'settings' && (
        <div className="consult-card-list">
          {(grouped[tab] || []).length === 0 ? (
            <div className="empty-consult">No consultations in this list.</div>
          ) : (
            grouped[tab].map((booking) => (
              <BookingCard
                key={booking._id}
                booking={booking}
                counterpart={booking.clientId}
                isAdvocate
                onChanged={load}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdvocateConsultationPanel;
export { BookingCard };
